/**
 * 資料請求フォーム（#document-request-form）のクライアント側バリデーション
 * visit-form-validation.js と同様の visit__fieldError / is-invalid で表示する
 */
import { isValidPhoneTel, PHONE_TEL_FORMAT_MESSAGE } from './phone-validation.js';
import { isValidEmail } from './email-validation.js';
const ZIP_RE = /^\d{3}-?\d{4}$/;

function trimValue(id) {
	const el = document.getElementById(id);
	return el ? el.value.trim() : '';
}

function clearErrorForControl(form, controlId) {
	form.querySelectorAll(`.visit__fieldError[data-for="${controlId}"]`).forEach((n) => n.remove());
	const control = document.getElementById(controlId);
	if (!control) return;
	control.removeAttribute('aria-invalid');
	control.classList.remove('is-invalid');
	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) wrap.classList.remove('is-invalid');
	const schedule = control.closest('.visit__schedule');
	if (schedule) schedule.classList.remove('is-invalid');
	const consent = control.closest('.visit__consent');
	if (consent) consent.classList.remove('is-invalid');
}

export function clearDocumentRequestFormErrors(form) {
	form.querySelectorAll('.visit__fieldError').forEach((n) => n.remove());
	form.querySelectorAll('.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__inputWrap--date.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__schedule.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('.visit__consent.is-invalid').forEach((n) => n.classList.remove('is-invalid'));
	form.querySelectorAll('[aria-invalid="true"]').forEach((n) => n.removeAttribute('aria-invalid'));
}

function mountErrorParagraph(controlId, message) {
	const p = document.createElement('p');
	p.className = 'visit__fieldError';
	p.setAttribute('data-for', controlId);
	p.setAttribute('role', 'alert');
	p.textContent = message;
	return p;
}

function showFieldError(form, controlId, message) {
	const control = document.getElementById(controlId);
	if (!control) return;

	clearErrorForControl(form, controlId);

	control.setAttribute('aria-invalid', 'true');
	control.classList.add('is-invalid');

	const wrap = control.closest('.visit__inputWrap--date');
	if (wrap) {
		wrap.classList.add('is-invalid');
		wrap.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	const schedule = control.closest('.visit__schedule');
	if (schedule) {
		schedule.classList.add('is-invalid');
		schedule.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
		return;
	}

	if (control.type === 'checkbox' && control.id === 'docreq-privacy') {
		const consent = control.closest('.visit__consent');
		if (consent) {
			consent.classList.add('is-invalid');
			consent.appendChild(mountErrorParagraph(controlId, message));
		}
		return;
	}

	if (control.id === 'docreq-name-family' || control.id === 'docreq-name-given') {
		const grid = control.closest('.visit__fieldGrid--name');
		if (grid) {
			grid.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
			return;
		}
	}

	if (control instanceof HTMLFieldSetElement && control.id === 'docreq-inquiry-fieldset') {
		control.appendChild(mountErrorParagraph(controlId, message));
		return;
	}

	control.insertAdjacentElement('afterend', mountErrorParagraph(controlId, message));
}

function focusFirst(controlId) {
	const el = document.getElementById(controlId);
	if (!el) return;
	if (el instanceof HTMLFieldSetElement) {
		const first = el.querySelector('input[type="checkbox"]');
		if (first instanceof HTMLElement) {
			first.focus();
		}
		return;
	}
	const schedule = el.closest('.visit__schedule');
	if (schedule instanceof HTMLElement) {
		const focusable = schedule.querySelector('.visit__scheduleArrow, .visit__scheduleSlot');
		if (focusable instanceof HTMLElement) {
			focusable.focus();
			return;
		}
	}
	const fpRoot = el.closest('.flatpickr');
	if (fpRoot) {
		const alt = fpRoot.querySelector('.flatpickr-alt-input');
		(alt || el).focus();
		return;
	}
	el.focus();
}

/**
 * @returns {boolean}
 */
export function validateDocumentRequestForm(form) {
	clearDocumentRequestFormErrors(form);

	const errors = [];

	const inquiryChecked = form.querySelectorAll('input[name="docreq_inquiry[]"]:checked');
	if (!inquiryChecked.length) {
		errors.push(['docreq-inquiry-fieldset', 'お問合せ内容を1つ以上選択してください。']);
	}

	const family = trimValue('docreq-name-family');
	const given = trimValue('docreq-name-given');
	if (!family && !given) {
		errors.push(['docreq-name-family', '性・名を入力してください。']);
	} else if (!family) {
		errors.push(['docreq-name-family', '性を入力してください。']);
	} else if (!given) {
		errors.push(['docreq-name-given', '名を入力してください。']);
	}

	const telRaw = trimValue('docreq-tel');
	if (telRaw && !isValidPhoneTel(telRaw)) {
		errors.push(['docreq-tel', PHONE_TEL_FORMAT_MESSAGE]);
	}

	const email = trimValue('docreq-email');
	if (!email) {
		errors.push(['docreq-email', 'メールアドレスを入力してください。']);
	} else if (!isValidEmail(email)) {
		errors.push(['docreq-email', 'メールアドレスの形式が正しくありません。']);
	}

	const zipRaw = trimValue('docreq-zip');
	const zipClean = zipRaw.replace(/[〒\s]/g, '');
	if (!zipRaw) {
		errors.push(['docreq-zip', '郵便番号を入力してください。']);
	} else if (!ZIP_RE.test(zipClean)) {
		errors.push(['docreq-zip', '郵便番号は7桁の半角数字で入力してください（ハイフン可）。']);
	}

	const pref = trimValue('docreq-pref');
	if (!pref) {
		errors.push(['docreq-pref', '都道府県を選択してください。']);
	}

	const city = trimValue('docreq-city');
	if (!city) {
		errors.push(['docreq-city', '市区町村を入力してください。']);
	}

	const street = trimValue('docreq-street');
	if (!street) {
		errors.push(['docreq-street', '町名・番地を入力してください。']);
	}

	const privacy = form.querySelector('#docreq-privacy');
	if (!privacy || !privacy.checked) {
		errors.push(['docreq-privacy', 'プライバシーポリシーに同意してください。']);
	}

	if (errors.length === 0) {
		return true;
	}

	for (const [id, msg] of errors) {
		showFieldError(form, id, msg);
	}
	focusFirst(errors[0][0]);
	return false;
}

export function initDocumentRequestFormValidation(form) {
	if (!form || form.id !== 'document-request-form') return;

	const onEdit = (e) => {
		const t = e.target;
		if (!t || !form.contains(t)) return;
		if (t.id) {
			clearErrorForControl(form, t.id);
		}
		if (t.matches?.('input[name="docreq_inquiry[]"]')) {
			clearErrorForControl(form, 'docreq-inquiry-fieldset');
		}
	};

	form.addEventListener('input', onEdit);
	form.addEventListener('change', onEdit);
}
